import { useState } from "react";
import { Link } from "react-router-dom";
import { directus } from "@/providers/directus";
import { PageHead } from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useBrand } from "@/hooks/useBrand";
import { usePreferences } from "@/hooks/usePreferences";
import { notifySaved } from "@/lib/notifySaved";
import { AccountSettingsDialog } from "@/components/AccountSettingsDialog";
import { NotificationSettingsDialog } from "@/components/notifications/NotificationSettingsDialog";
import { ShortcutsDialog } from "@/components/ShortcutsDialog";
import {
  Users, Mail, Signpost, ShieldCheck, Plug, KeyRound,
  Bell, Keyboard, UserCog, Palette, Moon, Sun, Monitor,
  ExternalLink, Sparkles
} from "lucide-react";

export function SettingsOverview() {
  const brand = useBrand();
  const { prefs, setPref } = usePreferences();

  const [accountOpen, setAccountOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  const [brandName, setBrandName] = useState(() => brand.name);
  const [brandDescriptor, setBrandDescriptor] = useState(() => brand.descriptor || "");
  const [editingBrand, setEditingBrand] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);

  const handleSaveBrand = async () => {
    setSavingBrand(true);
    try {
      const res: any = await directus.request(() => ({
        path: "/portal/brand",
        method: "PATCH",
        body: JSON.stringify({
          name: brandName.trim(),
          descriptor: brandDescriptor.trim(),
          color: tintColor,
        }),
        headers: { "Content-Type": "application/json" },
      }));
      const d = res?.data ?? res;
      if (d) {
        setBrandName(d.name);
        setBrandDescriptor(d.descriptor);
        setEditingBrand(false);
        notifySaved("Brand settings updated across Cordite");
        window.location.reload();
      }
    } catch (e: any) {
      notifySaved(e?.errors?.[0]?.message || e?.message || "Failed to save brand settings");
    } finally {
      setSavingBrand(false);
    }
  };

  const currentTheme = (prefs?.theme as "system" | "light" | "dark") || "system";
  const [tintColor, setTintColor] = useState<string>(() => (prefs?.brand_tint as string) || "#0f172a");

  const handleThemeChange = (mode: "system" | "light" | "dark") => {
    setPref("theme", mode);
    notifySaved(`Theme set to ${mode}`);
    // Sync class on document
    if (mode === "dark") {
      document.documentElement.classList.add("dark");
    } else if (mode === "light") {
      document.documentElement.classList.remove("dark");
    } else {
      const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
      document.documentElement.classList.toggle("dark", isDark);
    }
  };

  const handleSaveTint = () => {
    setPref("brand_tint", tintColor);
    notifySaved("Brand tint color saved");
  };

  const CONFIG_SECTIONS = [
    {
      category: "Administration & Infrastructure",
      items: [
        {
          title: "User Management & Permissions",
          desc: "Staff accounts, roles, access matrix, client hierarchy scoping, and user masquerade.",
          to: "/settings/users",
          icon: Users,
          badge: "RBAC",
        },
        {
          title: "Outbound Email & SMTP",
          desc: "Postmark SMTP relay, sender addresses, host configurations, and invitation mailers.",
          to: "/settings/email",
          icon: Mail,
          badge: "Postmark",
        },
        {
          title: "Client Routing Rules",
          desc: "Keyword matching, client disambiguation, and channel message classification.",
          to: "/settings/routing",
          icon: Signpost,
          badge: "Rules",
        },
        {
          title: "Immutable Audit Log",
          desc: "Timestamped changelog of all financial, task, and data modifications.",
          to: "/settings/audit",
          icon: ShieldCheck,
          badge: "Security",
        },
      ],
    },
    {
      category: "Integrations & Tools",
      items: [
        {
          title: "LLM, MCP & API Tokens",
          desc: "Static access tokens for Claude Code, Cursor, and external scripts with granular project and task visibility scopes.",
          to: "/connections",
          icon: Plug,
          badge: "MCP",
        },
        {
          title: "Two-Factor Authentication",
          desc: "Mandatory TOTP authenticator security enforced on staff and client accounts.",
          action: () => setAccountOpen(true),
          icon: KeyRound,
          badge: "2FA",
        },
        {
          title: "Notification Schedules & Quiet Hours",
          desc: "In-app alerts, quiet hour delivery windows, and daily briefing digests.",
          action: () => setNotifOpen(true),
          icon: Bell,
          badge: "Alerts",
        },
        {
          title: "Keyboard Shortcuts",
          desc: "Customizable global key bindings for row navigation, quick filters, and modal controls.",
          action: () => setShortcutsOpen(true),
          icon: Keyboard,
          badge: "Hotkeys",
        },
      ],
    },
  ];

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHead
        title="Settings"
        subtitle="System configuration, brand identity, integrations, and administration directory."
      />

      {/* Brand & Theme Identity Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Palette className="w-4 h-4 text-primary" />
            <span>Firm Branding &amp; Appearance</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Logo and Name Preview */}
            <div className="p-4 rounded-lg border border-border bg-card flex flex-col gap-2.5">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                  Active Brand
                </span>
                <button
                  type="button"
                  onClick={() => setEditingBrand(!editingBrand)}
                  className="text-[11px] text-primary hover:underline cursor-pointer"
                >
                  {editingBrand ? "Cancel" : "Edit"}
                </button>
              </div>

              {editingBrand ? (
                <div className="flex flex-col gap-2 pt-1">
                  <Input
                    value={brandName}
                    onChange={(e) => setBrandName(e.target.value)}
                    placeholder="Brand name (e.g. Cordite, Studio Dome)"
                    className="h-8 text-xs"
                  />
                  <Input
                    value={brandDescriptor}
                    onChange={(e) => setBrandDescriptor(e.target.value)}
                    placeholder="Descriptor (e.g. Sweat & Sterling)"
                    className="h-8 text-xs"
                  />
                  <Button
                    size="sm"
                    onClick={handleSaveBrand}
                    disabled={savingBrand || !brandName.trim()}
                    className="h-8 text-xs mt-1"
                  >
                    {savingBrand ? "Saving…" : "Save Brand"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-3">
                  <img
                    src={brand.logoUrl}
                    alt={brand.name}
                    className="w-10 h-10 object-contain rounded border border-border bg-muted p-1"
                  />
                  <div className="min-w-0">
                    <div className="font-semibold text-foreground text-sm truncate">{brand.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{brand.descriptor || "Firm wordmark"}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Appearance Mode */}
            <div className="p-4 rounded-lg border border-border bg-card flex flex-col gap-2.5">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                Color Scheme
              </span>
              <div className="inline-flex rounded-md border border-border p-1 bg-muted/30">
                <button
                  type="button"
                  onClick={() => handleThemeChange("system")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1 text-xs rounded transition-colors ${currentTheme === "system" ? "bg-card text-foreground font-medium shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Monitor className="w-3.5 h-3.5" />
                  <span>System</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleThemeChange("light")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1 text-xs rounded transition-colors ${currentTheme === "light" ? "bg-card text-foreground font-medium shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Sun className="w-3.5 h-3.5" />
                  <span>Light</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleThemeChange("dark")}
                  className={`flex-1 flex items-center justify-center gap-1.5 py-1 text-xs rounded transition-colors ${currentTheme === "dark" ? "bg-card text-foreground font-medium shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                >
                  <Moon className="w-3.5 h-3.5" />
                  <span>Dark</span>
                </button>
              </div>
            </div>

            {/* Brand Tint Color */}
            <div className="p-4 rounded-lg border border-border bg-card flex flex-col gap-2.5">
              <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">
                Brand Tint
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={tintColor}
                  onChange={(e) => setTintColor(e.target.value)}
                  className="h-8 w-8 rounded border border-border cursor-pointer p-0.5 bg-transparent"
                />
                <Input
                  value={tintColor}
                  onChange={(e) => setTintColor(e.target.value)}
                  className="h-8 font-mono text-xs flex-1 uppercase"
                />
                <Button size="sm" variant="ghost" onClick={handleSaveTint} className="h-8 text-xs">
                  Save
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Directory of Settings Sections */}
      {CONFIG_SECTIONS.map((group) => (
        <div key={group.category} className="space-y-3">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {group.category}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            {group.items.map((item) => {
              const Content = (
                <div className="p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors flex items-start gap-3.5 h-full">
                  <div className="w-8 h-8 rounded-md bg-secondary flex items-center justify-center shrink-0 mt-0.5 text-foreground">
                    <item.icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-foreground text-[13px] truncate">
                        {item.title}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="gray" className="text-[10px] font-mono px-1.5 py-0">
                          {item.badge}
                        </Badge>
                        <ExternalLink className="w-3 h-3 text-muted-foreground opacity-60" />
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                      {item.desc}
                    </p>
                  </div>
                </div>
              );

              if ("to" in item && item.to) {
                return (
                  <Link key={item.title} to={item.to} className="block no-underline">
                    {Content}
                  </Link>
                );
              }

              return (
                <button
                  key={item.title}
                  type="button"
                  onClick={item.action}
                  className="w-full text-left cursor-pointer p-0 bg-transparent border-0"
                >
                  {Content}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <AccountSettingsDialog open={accountOpen} onClose={() => setAccountOpen(false)} />
      <NotificationSettingsDialog open={notifOpen} onClose={() => setNotifOpen(false)} />
      <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
